<template>
    <UModal :model-value="open" @update:model-value="$emit('update:open', $event)">
        <UCard :ui="{ rounded: 'rounded-lg', body: { base: 'overflow-visible' } }">
            <template #header>
                <div class="flex items-center justify-between">
                    <h3 class="text-base font-medium leading-6 text-gray-800">Send Recommendation</h3>
                    <UButton color="gray" variant="ghost" icon="i-ph-x-bold" class="-my-1" @click="dismiss()" />
                </div>
            </template>
            <div class="border border-gray-200 shadow-sm rounded-xl overflow-hidden">
                <div class="flex items-center space-x-3 bg-gray-100 py-4 px-5">
                    <UIcon name="i-ph-whatsapp-logo-duotone" class="size-6 text-emerald-600" />
                    <div class="leading-5">
                        <p class="text-[15px] font-semibold font-mono text-emerald-700">+{{content.phoneNumber}}</p>
                        <p v-if="content.recipientName" class="text-xs text-gray-500">{{ content.recipientName }}</p>
                    </div>
                </div>
                <div class="p-5 border-t text-sm bg-gray-50">
                    <p>Hello, please find below your recommendation from SAMU Liban:</p>
                    <br />
                    <p class="whitespace-pre-wrap">{{ content.consultation }}</p>
                    <br />
                    <p>-- Ref. #{{ content.reportNumber }}</p>
                </div>
            </div>
            <template #footer>
                <div class="flex items-center justify-end space-x-2">
                    <UButton @click="dismiss()" :disabled="confirming" size="lg" color="white"> Cancel </UButton>
                    <UButton @click="confirm()" :loading="confirming" size="lg" color="black"> Send </UButton>
                </div>
            </template>
        </UCard>
    </UModal>
</template>

<script setup lang="ts">
import type { CallReport } from '~/types/callReport'
import type { Customer } from '~/types/customer'

const emit = defineEmits<{
    'update:open': [boolean]
    'reload': [void]
}>()

const props = defineProps<{
    open: boolean
    report?: CallReport
    recipient?: Customer
}>()

const toast = useToast()
const client = useSupabaseClient()
const confirming = ref<boolean>(false)

const isCaller = computed(() => !!props.recipient && props.recipient.id !== props.report?.customer?.id)

const content = computed(() => ({
    phoneNumber: props.recipient?.phone?.replace(/[\+\s]/g, ''),
    recipientName: isCaller.value
        ? `Caller: ${[props.recipient?.first_name, props.recipient?.last_name].filter(Boolean).join(' ')}`
        : undefined,
    reportNumber: props.report?.number?.toString().padStart(8, '0'),
    consultation: props.report?.consultation,
}))

watch(() => props.open, (open) => {
    if (open) {
        confirming.value = false
    }
})

async function confirm() {
    if (props.report?.id) {
        confirming.value = true

        try {
            const { error } = await client.functions.invoke('send-consultation', {
                body: { id: props.report.id }
            })

            if (error) throw error
            
            toast.add({
                title: 'Recommendation sent successfully!',
                color: 'green',
                icon: 'i-ph-check-circle-duotone'
            })

            emit('reload')
            dismiss()
        } catch (error: any) {
            let title = 'Could not send recommendation, please try again later.'
            if (error.status === 450) title = 'Error: Invalid call report'
            if (error.status === 451) title = 'Phone number not registered on Whatsapp'
            
            toast.add({
                title,
                color: 'red',
                icon: 'i-ph-warning-duotone'
            })
            
            dismiss()
        } finally {
            confirming.value = false
        }
    }
}

function dismiss() {
    emit('update:open', false)
}
</script>
